import { Config } from '../config';
import { DisruptionAction } from '../MainPanel';
import { convertDisruptionFromApi } from './converters';

interface WsCtx {
  ws: WebSocket | null;
  store: React.Dispatch<DisruptionAction> | null;
  ping_timeout: any;
}
type DisruptionMsgType = 'PING' | 'UPDATE' | 'REMOVE';
interface DisruptionMsgBase {
  type: DisruptionMsgType;
}
interface DisruptionMsgUpdate extends DisruptionMsgBase {
  type: 'UPDATE';
  payload: DisruptionFromApi[];
}
interface DisruptionMsgRemove extends DisruptionMsgBase {
  type: 'REMOVE';
  /** Disruption ID. */
  payload: number[];
}
interface DisruptionMsgPing extends DisruptionMsgBase {
  type: 'PING';
}

type DisruptionMsg =
  | DisruptionMsgRemove
  | DisruptionMsgUpdate
  | DisruptionMsgPing;

let reconnect_delay = 5000;
const reconnect_delay_max = 30000;
let reconnect_timeout: any = undefined;

const ctx: WsCtx = {
  ws: null,
  store: null,
  ping_timeout: 0,
};

const heartbeat = (): void => {
  if (ctx.ping_timeout) {
    clearTimeout(ctx.ping_timeout);
  }

  ctx.ping_timeout = setTimeout(() => {
    ctx.ws && ctx.ws.close();
  }, 30000 + 2000);
};

const onping = (): void => {
  if (!ctx.ws || ctx.ws.readyState !== ctx.ws.OPEN) {
    return;
  }
  heartbeat();
  ctx.ws.send(JSON.stringify({ type: 'PONG' }));
};

const connectWebSocket = (uri: string): void => {
  if (!ctx.ws) {
    //TODO: Maybe add a spinner? Which should be triggered here.
  }
  ctx.ws = new WebSocket(uri);

  ctx.ws.onmessage = (event: MessageEvent): any => {
    const msg: DisruptionMsg = JSON.parse(event.data);

    if (
      typeof msg !== 'object' ||
      !Object.prototype.hasOwnProperty.call(msg, 'type') ||
      !msg.type
    ) {
      console.warn('Invalid message recieved on disruption socket.');
      return;
    }
    if (!ctx.store) {
      console.error('Recieved disruption message before store init. Ignoring.');
      return;
    }

    const type = msg.type;

    switch (msg.type) {
      case 'PING': {
        onping();
        return;
      }
      case 'UPDATE': {
        if (
          !Object.prototype.hasOwnProperty.call(msg, 'payload') ||
          !msg.payload
        ) {
          console.error("Missing payload prop on disruption 'update' message.");
          //TODO: Verify payload contents?
          return;
        }
        ctx.store({
          type: 'update',
          payload: msg.payload.map(convertDisruptionFromApi),
        });
        return;
      }
      case 'REMOVE': {
        if (
          !Object.prototype.hasOwnProperty.call(msg, 'payload') ||
          !msg.payload
        ) {
          console.error("Missing payload prop on disruption 'remove' message.");
          //TODO: Verify payload contents
          return;
        }

        /* if (!Number.isFinite(msg.payload)) {
          console.error('Invalid remove payload.');
          return;
        } */
        ctx.store({ type: 'remove', payload: msg.payload });
        return;
      }
      default: {
        console.error(
          "Invalid message type recieved on disruption socket: '" + type + "'.",
        );
        return;
      }
    }
  };

  ctx.ws.onclose = () => {
    console.log('On close.');
    reconnect_timeout && clearTimeout(reconnect_timeout);
    reconnect_timeout = setTimeout(
      () => connectWebSocket(uri),
      reconnect_delay,
    );
    reconnect_delay =
      reconnect_delay >= reconnect_delay_max
        ? reconnect_delay_max
        : reconnect_delay + 1000;
  };
  ctx.ws.onerror = (err) => {
    console.error(err);
    ctx.ws && ctx.ws.close();
    reconnect_timeout && clearTimeout(reconnect_timeout);
    reconnect_timeout = setTimeout(
      () => connectWebSocket(uri),
      reconnect_delay,
    );
    reconnect_delay =
      reconnect_delay >= reconnect_delay_max
        ? reconnect_delay_max
        : reconnect_delay + 1000;
  };
};

const prepare = (store: React.Dispatch<DisruptionAction>): void => {
  ctx.store = store;
};

const start = (config: Config): void => {
  if (ctx.ws) {
    console.warn(
      'Tried to start disruption websocket when a socket is already started.',
    );
    return;
  }
  connectWebSocket(config.notifications.disruptions.wss);
};

export default {
  prepare,
  start,
};
